import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import socket from "../connection";
import { useAuthcontext } from "../Contexts/AuthContext";
import { useAppContext } from "../Contexts/AppContext";
import EmojiPicker from "emoji-picker-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSmile, faPaperPlane, faCheck, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

function Chat() {
  const { room } = useParams();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const { TheatorUser } = useAuthcontext();
  const { type } = useAppContext();

  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    const receiveEvent = type === "manual" ? "manualSessions-receiveMessage" : "receiveMessage";
    const typingEvent = type === "manual" ? "manualSessions-userTyping" : "userTyping";
    const stopTypingEvent = type === "manual" ? "manualSessions-stopTyping" : "stopTyping";

    socket.on(receiveEvent, (msg) => {
      setMessages((prev) => [...prev, { ...msg, readBy: [] }]);
      
      // Notify others that we've seen this message
      if (msg.email !== TheatorUser?.email) {
        socket.emit("messageSeen", { room, messageId: msg.id, userName: TheatorUser?.name, profilePicture: TheatorUser?.profilePicture });
      }

      setTypingUsers((prev) => {
        const next = new Set(prev);
        next.delete(msg.name);
        return next;
      });
    });

    socket.on("userReadMessage", (data) => {
      setMessages((prev) => prev.map(m => {
        if (m.id === data.messageId) {
          const alreadyRead = m.readBy?.some(u => u.name === data.userName);
          if (!alreadyRead) {
            return { ...m, readBy: [...(m.readBy || []), { name: data.userName, avatar: data.profilePicture }] };
          }
        }
        return m;
      }));
    });

    socket.on(typingEvent, (data) => {
      if (data.email !== TheatorUser?.email) {
        setTypingUsers((prev) => new Set(prev).add(data.name));
      }
    });

    socket.on(stopTypingEvent, (data) => {
      setTypingUsers((prev) => {
        const next = new Set(prev);
        next.delete(data.name);
        return next;
      });
    });

    return () => {
      socket.off(receiveEvent);
      socket.off("userReadMessage");
      socket.off(typingEvent);
      socket.off(stopTypingEvent);
    };
  }, [type, TheatorUser?.email, TheatorUser?.name, TheatorUser?.profilePicture, room]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, typingUsers]);

  const handleTyping = (e) => {
    setMessage(e.target.value);
    
    if (!isTyping) {
      setIsTyping(true);
      const event = type === "manual" ? "manualSessions-typing" : "typing";
      socket.emit(event, { room, name: TheatorUser?.name, email: TheatorUser?.email });
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      const event = type === "manual" ? "manualSessions-stopTyping" : "stopTyping";
      socket.emit(event, { room, name: TheatorUser?.name });
    }, 2000);
  };

  const sendMessage = () => {
    if (message.trim()) {
      const newMessage = {
        id: Date.now() + "-" + Math.random().toString(36).substr(2, 9),
        name: TheatorUser?.name || "Anonymous",
        email: TheatorUser?.email || "random@123gmail.com",
        profilePicture: TheatorUser?.profilePicture,
        text: message,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        readBy: []
      };

      const event = type === "manual" ? "manualSessions-chatMessage" : "chatMessage";
      socket.emit(event, { room, message: newMessage });

      setIsTyping(false);
      const stopEvent = type === "manual" ? "manualSessions-stopTyping" : "stopTyping";
      socket.emit(stopEvent, { room, name: TheatorUser?.name });

      setMessage("");
      setShowEmojiPicker(false);
    }
  };

  return (
    <div className="chat-container flex flex-col h-full border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden bg-[#0f172a] text-slate-100">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-md p-4 flex justify-between items-center border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
          <span className="font-bold tracking-wide">Live Chat</span>
        </div>
        <div className="text-[10px] text-slate-400 font-mono uppercase tracking-widest">{type} SESSION</div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 p-4 overflow-y-auto space-y-4 scroll-smooth custom-scrollbar" ref={messagesEndRef}>
        <AnimatePresence initial={false}>
          {messages.map((msg, index) => {
            const isMe = msg?.email === TheatorUser?.email;
            const readCount = msg.readBy?.length || 0;
            
            return (
              <motion.div
                key={msg.id || index}
                initial={{ opacity: 0, x: isMe ? 20 : -20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                className={`flex w-full group ${isMe ? "justify-end" : "justify-start"}`}
              >
                {!isMe && (
                  <img src={msg?.profilePicture} alt="pfp" className="w-8 h-8 rounded-full mr-2 self-end border border-slate-700 shadow-lg" />
                )}
                <div className={`relative px-4 py-2.5 rounded-2xl max-w-[80%] shadow-lg ${
                  isMe ? "bg-blue-600 text-white rounded-br-none" : "bg-slate-800 text-slate-100 rounded-bl-none border border-slate-700"
                }`}>
                  {!isMe && <div className="text-[10px] font-bold text-blue-400 mb-0.5">{msg.name}</div>}
                  <p className="text-sm leading-relaxed break-words">{msg.text}</p>
                  
                  <div className={`flex items-center gap-1.5 mt-1 justify-end opacity-70`}>
                    <span className="text-[9px] font-medium">{msg.time}</span>
                    {isMe && (
                      <div className="flex items-center gap-1">
                        {/* Status Checkmarks */}
                        <FontAwesomeIcon 
                          icon={faCheckDouble} 
                          className={`text-[9px] ${readCount > 0 ? "text-blue-300" : "text-slate-400"}`} 
                        />
                        
                        {/* Read By Avatars */}
                        {readCount > 0 && (
                          <div className="flex -space-x-1.5 ml-1 transition-all duration-300 group-hover:ml-2">
                            {msg.readBy.slice(0, 3).map((user, i) => (
                              <img 
                                key={i} 
                                src={user.avatar} 
                                title={`Read by ${user.name}`}
                                className="w-3.5 h-3.5 rounded-full border border-blue-600 shadow-sm ring-1 ring-white/10"
                                alt="" 
                              />
                            ))}
                            {readCount > 3 && (
                              <div className="w-3.5 h-3.5 rounded-full bg-blue-700 border border-blue-600 flex items-center justify-center text-[6px] font-bold ring-1 ring-white/10">
                                +{readCount - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Typing Indicator */}
        <AnimatePresence>
          {typingUsers.size > 0 && Array.from(typingUsers).map((user) => (
            <motion.div
              key={user}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center gap-2 text-slate-400 mb-2"
            >
              <div className="flex gap-1 px-3 py-2 bg-slate-800 rounded-full border border-slate-700">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </div>
              <span className="text-[10px] font-medium italic">{user} is typing...</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Input Area */}
      <div className="p-4 bg-slate-900/50 backdrop-blur-xl border-t border-slate-700/50 relative">
        <div className="flex items-end gap-2 bg-slate-800/80 rounded-[1.5rem] border border-slate-700 p-2 focus-within:border-blue-500/50 transition-all shadow-inner">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-2 text-slate-400 hover:text-blue-400 transition-colors"
          >
            <FontAwesomeIcon icon={faSmile} size="lg" />
          </button>
          
          <textarea
            rows="1"
            className="flex-1 bg-transparent border-none outline-none py-2 text-sm text-slate-200 placeholder:text-slate-500 resize-none max-h-32 custom-scrollbar"
            placeholder="Type your message..."
            value={message}
            onChange={handleTyping}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={sendMessage}
            className="bg-blue-600 p-3 rounded-2xl text-white shadow-lg shadow-blue-500/20 hover:bg-blue-500 transition-colors"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </motion.button>
        </div>

        {showEmojiPicker && (
          <div className="absolute bottom-full left-4 mb-4 z-50 shadow-2xl rounded-2xl overflow-hidden border border-slate-700" ref={emojiPickerRef}>
            <EmojiPicker
              onEmojiClick={(data) => setMessage(prev => prev + data.emoji)}
              theme="dark"
              width={300}
              height={350}
              skinTonesDisabled
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Chat;
